import { useThree } from '@react-three/fiber'
import { HeatmapMaterial } from 'features/assetViz/materials/HeatmapMaterial'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import {
  RenderLayerID,
  RenderOrder,
} from 'features/assetViz/utils/renderingDefines'
import { useCdfRawValues } from 'features/dataLayers'
import React from 'react'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMeasurementRange,
} from 'types'

export type TextureChunkSize = {
  xBinCount: number
  yBinCount: number
}

/**
 * A layer used to render a CDF heatmap on a component's surface
 * @param cdf                     The current CDF being rendered
 * @constructor
 */
export const HeatmapLayerChunk3D: React.FC<{
  cdf: GeckoComponentDataFrame
}> = ({ cdf }) => {
  //----------------------------------------------------------------------------
  const { gl } = useThree()
  const maxTextureSize = React.useMemo(
    () => gl.getContext().getParameter(gl.getContext().MAX_TEXTURE_SIZE),
    [gl]
  )

  const { buffer: cdfDataBuffer, isLoading: isLoadingCdfData } =
    useCdfRawValues(cdf)

  const { textureSizes, geometries, dataViews } = React.useMemo(() => {
    const dataWidthBinCount = cdf.data.binning.x_bin_count
    const dataHeightBinCount = cdf.data.binning.y_bin_count
    if (isLoadingCdfData) {
      return {
        textureSizes: [{ xBinCount: 1, yBinCount: 1 }],
        geometries: [cdf.data.geometry],
        dataViews: [cdfDataBuffer?.values.subarray(0, 1)],
      }
    }
    if (dataWidthBinCount * dataHeightBinCount <= maxTextureSize)
      return {
        textureSizes: [
          {
            xBinCount: dataWidthBinCount,
            yBinCount: dataHeightBinCount,
          },
        ],
        geometries: [cdf.data.geometry],
        dataViews: [cdfDataBuffer?.values],
      }

    return dataWidthBinCount > maxTextureSize
      ? chunkTextureBothHorizontallyAndVertically(
          maxTextureSize,
          cdf,
          cdfDataBuffer?.values
        )
      : chunkTextureVertically(maxTextureSize, cdf, cdfDataBuffer?.values)
  }, [cdfDataBuffer, cdf, maxTextureSize, isLoadingCdfData])

  const { bounds, geometry, wrapMode } = useSurfaceProps()

  const userData = React.useMemo(() => ({ cdf }), [cdf])

  return textureSizes.map((size, i) => (
    <mesh
      key={i}
      name={`${bounds.componentID}_heatmap_chunk`}
      layers={RenderLayerID.VisibleCollidable}
      geometry={geometry}
      renderOrder={RenderOrder.Heatmap}
      userData={userData}>
      <HeatmapMaterial
        key={i}
        // Chunk dimensions
        chunkBounds={bounds}
        doWrapX={wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX}
        doWrapY={wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY}
        // CDFs
        cdf={cdf}
        geometry={geometries[i]}
        textureWidth={size.xBinCount}
        textureHeight={size.yBinCount}
        cdfData={dataViews[i]}
        isLoading={isLoadingCdfData}
      />
    </mesh>
  ))
}

/**
 * If maxTextureSize is less than the number of bins along the x axis of the
 * data, then we have to split every row by the maxTextureSize and and render a
 * mesh for each chunk
 * @param maxTextureSize
 * @param cdf
 * @param cdfData
 * @returns
 */
function chunkTextureBothHorizontallyAndVertically(
  maxTextureSize: number,
  cdf: GeckoComponentDataFrame,
  cdfData: Float32Array | undefined
): {
  dataViews: (Float32Array | undefined)[]
  textureSizes: TextureChunkSize[]
  geometries: GeckoComponentRect[]
} {
  const dataWidthBinCount = cdf.data.binning.x_bin_count
  const dataHeightBinCount = cdf.data.binning.y_bin_count
  const cdfGeometry = cdf.data.geometry

  const maxWidth = maxTextureSize
  const maxHeight = 1
  const binHeight = cdf.data.binning.bin_size.height
  const binWidth = cdf.data.binning.bin_size.width

  const textureHeight = binHeight
  const numBlocksVertical = dataHeightBinCount

  const textureWidth = binWidth.scale(maxWidth)
  const numBlocksHorizontal = Math.floor(dataWidthBinCount / maxWidth)

  let yStart = cdfGeometry.y.start
  const textureSizes: TextureChunkSize[] = []
  const geometries: GeckoComponentRect[] = []
  const dataViews: (Float32Array | undefined)[] = []
  for (let i = 0; i < numBlocksVertical; i++) {
    let xStart = cdfGeometry.x.start
    const yEnd = yStart.plus(textureHeight)
    for (let j = 0; j < numBlocksHorizontal; j++) {
      const xEnd = xStart.plus(textureWidth)
      textureSizes.push({
        xBinCount: maxWidth,
        yBinCount: maxHeight,
      })
      geometries.push(
        GeckoComponentRect.CreateFromRanges(
          cdfGeometry.componentID,
          GeckoMeasurementRange.CreateFromStartEnd(xStart, xEnd),
          GeckoMeasurementRange.CreateFromStartEnd(yStart, yEnd)
        )
      )
      const startIndex = i * dataWidthBinCount + j * maxWidth
      const endIndex = (i + 1) * dataWidthBinCount + (j + 1) * maxWidth
      dataViews.push(cdfData?.subarray(startIndex, endIndex))
      xStart = xEnd
    }
    if (dataWidthBinCount % maxWidth > 0) {
      textureSizes.push({
        xBinCount: dataWidthBinCount % maxWidth,
        yBinCount: maxHeight,
      })
      geometries.push(
        GeckoComponentRect.CreateFromRanges(
          cdfGeometry.componentID,
          GeckoMeasurementRange.CreateFromStartEnd(
            xStart,
            cdf.data.geometry.x.end
          ),
          GeckoMeasurementRange.CreateFromStartEnd(yStart, yEnd)
        )
      )
      const startIndex =
        i * dataWidthBinCount + (numBlocksHorizontal - 1) * maxWidth
      const endIndex = (i + 1) * dataWidthBinCount
      dataViews.push(cdfData?.subarray(startIndex, endIndex))
    }
    yStart = yEnd
  }
  return {
    textureSizes,
    dataViews,
    geometries,
  }
}

/**
 * If maxTextureSize is greater than the number of bins along the x axis of the
 * data, then we have to split vertically by the greatest number of whole rows we can
 * fit into the maxTextureSize and render a mesh for each chunk
 * @param maxTextureSize
 * @param cdf
 * @param cdfData
 * @returns
 */
function chunkTextureVertically(
  maxTextureSize: number,
  cdf: GeckoComponentDataFrame,
  cdfData: Float32Array | undefined
): {
  dataViews: (Float32Array | undefined)[]
  textureSizes: TextureChunkSize[]
  geometries: GeckoComponentRect[]
} {
  const dataWidthBinCount = cdf.data.binning.x_bin_count
  const dataHeightBinCount = cdf.data.binning.y_bin_count
  const cdfGeometry = cdf.data.geometry
  const maxHeight = Math.floor(maxTextureSize / dataWidthBinCount)
  const binHeight = cdf.data.binning.bin_size.height
  const textureHeight = binHeight.scale(maxHeight)
  let yStart = cdfGeometry.y.start
  const textureSizes: TextureChunkSize[] = []
  const geometries: GeckoComponentRect[] = []
  const dataViews: (Float32Array | undefined)[] = []
  for (let i = 0; i < Math.floor(dataHeightBinCount / maxHeight); i++) {
    textureSizes.push({
      xBinCount: dataWidthBinCount,
      yBinCount: maxHeight,
    })
    const yEnd = yStart.plus(textureHeight)
    geometries.push(
      GeckoComponentRect.CreateFromRanges(
        cdfGeometry.componentID,
        cdfGeometry.x,
        GeckoMeasurementRange.CreateFromStartEnd(yStart, yEnd)
      )
    )
    dataViews.push(
      cdfData?.subarray(
        i * maxHeight * dataWidthBinCount,
        (i + 1) * maxHeight * dataWidthBinCount
      )
    )
    yStart = yEnd
  }
  if (dataHeightBinCount % maxHeight > 0) {
    const leftoverHeight = dataHeightBinCount % maxHeight
    textureSizes.push({
      xBinCount: dataWidthBinCount,
      yBinCount: leftoverHeight,
    })
    geometries.push(
      GeckoComponentRect.CreateFromRanges(
        cdfGeometry.componentID,
        cdfGeometry.x,
        GeckoMeasurementRange.CreateFromStartEnd(
          yStart,
          cdf.data.geometry.y.end
        )
      )
    )
    dataViews.push(
      cdfData?.subarray(
        Math.floor(dataHeightBinCount / maxHeight) *
          maxHeight *
          dataWidthBinCount,
        dataHeightBinCount * dataWidthBinCount
      )
    )
  }
  return {
    textureSizes,
    dataViews,
    geometries,
  }
}
